import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Avatar,
  Chip,
  CircularProgress,
  Alert,
  Container,
  Fade,
  Slide,
  Grow,
} from "@mui/material";
import {
  School as SchoolIcon,
  ArrowBack as ArrowBackIcon,
} from "@mui/icons-material";
import axios from "axios";
import { ApplicationFormModal, ApplicationFormData } from "../components/Modals/ApplicationFormModal";

interface SchoolData {
  id: number;
  name: string;
  mediums: string[];
}

const SchoolSelection: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [selectedSchool, setSelectedSchool] = useState<number | null>(null);
  const [schools, setSchools] = useState<SchoolData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [applicationModalOpen, setApplicationModalOpen] = useState(false);
  const [selectedSchoolData, setSelectedSchoolData] = useState<SchoolData | null>(null);

  // Fetch schools data
  useEffect(() => {
    const fetchSchools = async () => {
      try {
        setIsLoading(true);
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/help`);
        setSchools(response.data);
        setError(null);
      } catch (err) {
        setError('Failed to load schools. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchSchools();
  }, []);

  const handleSchoolSelect = (school: SchoolData) => {
    setSelectedSchool(school.id);
    setSelectedSchoolData(school);
    setApplicationModalOpen(true);
  };

  const handleApplicationSubmit = (formData: ApplicationFormData) => {
    // Here you can handle the form submission
    // For now, we'll just close the modal
    setApplicationModalOpen(false);
    setSelectedSchool(null);
    setSelectedSchoolData(null);
  };

  const handleApplicationModalClose = () => {
    setApplicationModalOpen(false);
    setSelectedSchool(null);
    setSelectedSchoolData(null);
  };

  const handleBackToLogin = () => {
    if (isAuthenticated) {
      // If user is already logged in, go to their dashboard
      navigate("/");
    } else {
      // If user is not logged in, go to login page
      navigate("/login");
    }
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        }}
      >
        <Box sx={{ textAlign: "center" }}>
          <CircularProgress size={60} sx={{ color: "white", mb: 2 }} />
          <Typography variant="h6" sx={{ color: "white" }}>
            Loading Schools...
          </Typography>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          p: 2,
        }}
      >
        <Alert severity="error" sx={{ maxWidth: 500 }}>
          <Typography variant="h6" gutterBottom>
            Error Loading Schools
          </Typography>
          <Typography variant="body2">
            Unable to load the list of schools. Please try again later.
          </Typography>
          <Button
            variant="contained"
            onClick={() => window.location.reload()}
            sx={{ mt: 2 }}
          >
            Retry
          </Button>
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Animated Background Elements */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          opacity: 0.1,
          background:
            'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.4"%3E%3Ccircle cx="30" cy="30" r="2"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
          animation: "float 20s ease-in-out infinite",
          "@keyframes float": {
            "0%, 100%": { transform: "translateY(0px)" },
            "50%": { transform: "translateY(-20px)" },
          },
        }}
      />

      {/* Floating School Icons */}
      <Box
        sx={{
          position: "absolute",
          top: "10%",
          left: "10%",
          animation: "bounce 3s ease-in-out infinite",
          "@keyframes bounce": {
            "0%, 100%": { transform: "translateY(0px) rotate(0deg)" },
            "50%": { transform: "translateY(-30px) rotate(180deg)" },
          },
        }}
      >
        <SchoolIcon sx={{ fontSize: 60, color: "rgba(255,255,255,0.3)" }} />
      </Box>

      <Box
        sx={{
          position: "absolute",
          top: "20%",
          right: "15%",
          animation: "bounce 4s ease-in-out infinite 1s",
        }}
      >
        <SchoolIcon sx={{ fontSize: 40, color: "rgba(255,255,255,0.2)" }} />
      </Box>

      <Box
        sx={{
          position: "absolute",
          bottom: "15%",
          left: "20%",
          animation: "bounce 3.5s ease-in-out infinite 0.5s",
        }}
      >
        <SchoolIcon sx={{ fontSize: 50, color: "rgba(255,255,255,0.25)" }} />
      </Box>

      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 10, py: 4 }}>
        {/* Header */}
        <Fade in timeout={1000}>
          <Box sx={{ textAlign: "center", mb: 4 }}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={handleBackToLogin}
              sx={{
                color: "white",
                mb: 2,
                "&:hover": {
                  background: "rgba(255, 255, 255, 0.1)",
                },
              }}
            >
              {isAuthenticated ? "Back to Dashboard" : "Back to Login"}
            </Button>
            <Typography
              variant="h3"
              fontWeight="bold"
              sx={{
                color: "white",
                mb: 2,
                textShadow: "0 2px 4px rgba(0,0,0,0.3)",
              }}
            >
              Select Your School
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: "rgba(255, 255, 255, 0.9)",
                maxWidth: 600,
                mx: "auto",
              }}
            >
              Choose your school and preferred medium of instruction to proceed with the application form
            </Typography>
          </Box>
        </Fade>

        {/* Schools Grid */}
        <Slide direction="up" in timeout={1500}>
          <Grid container spacing={3}>
            {schools?.map((school, index) => (
              <Grid item xs={12} sm={6} md={4} key={school.id}>
                <Grow in timeout={2000 + index * 200}>
                  <Card
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      background: "rgba(255, 255, 255, 0.95)",
                      backdropFilter: "blur(20px)",
                      borderRadius: 3,
                      boxShadow: "0 25px 50px rgba(0,0,0,0.3)",
                      border: "1px solid rgba(255, 255, 255, 0.2)",
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                      "&:hover": {
                        transform: "translateY(-8px)",
                        boxShadow: "0 35px 70px rgba(0,0,0,0.4)",
                      },
                    }}
                  >
                    <CardContent sx={{ p: 3, flexGrow: 1, display: "flex", flexDirection: "column" }}>
                      {/* School Header */}
                      <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                        <Avatar
                          sx={{
                            bgcolor: "primary.main",
                            width: 60,
                            height: 60,
                            mr: 2,
                            boxShadow: "0 4px 12px rgba(25, 118, 210, 0.3)",
                          }}
                        >
                          <SchoolIcon />
                        </Avatar>
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography
                            variant="h5"
                            fontWeight="bold"
                            sx={{
                              color: "primary.main",
                              mb: 1,
                              lineHeight: 1.2,
                            }}
                          >
                            {school.name}
                          </Typography>
                        </Box>
                      </Box>

                      {/* School Details - Simplified */}
                      <Box sx={{ mb: 3, flexGrow: 1 }}>
                        {/* Mediums */}
                        {school.mediums && school.mediums.length > 0 && (
                          <Box sx={{ mt: 2 }}>
                            <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 1, fontWeight: "bold" }}>
                              Available Mediums:
                            </Typography>
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                              {school.mediums.map((medium, idx) => (
                                <Chip
                                  key={idx}
                                  label={medium}
                                  size="medium"
                                  color="primary"
                                  variant="filled"
                                  sx={{ 
                                    fontSize: "0.8rem",
                                    fontWeight: "bold",
                                    "& .MuiChip-label": {
                                      px: 2
                                    }
                                  }}
                                />
                              ))}
                            </Box>
                          </Box>
                        )}
                      </Box>

                      {/* Select Button */}
                      <Button
                        variant="contained"
                        fullWidth
                        onClick={() => handleSchoolSelect(school)}
                        disabled={selectedSchool === school.id}
                        sx={{
                          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                          borderRadius: 2,
                          py: 1.5,
                          fontWeight: "bold",
                          fontSize: 16,
                          boxShadow: "0 8px 25px rgba(102, 126, 234, 0.4)",
                          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                          "&:hover": {
                            background: "linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)",
                            boxShadow: "0 12px 35px rgba(102, 126, 234, 0.6)",
                            transform: "translateY(-2px)",
                          },
                          "&:active": {
                            transform: "translateY(0px)",
                          },
                        }}
                      >
                        {selectedSchool === school.id ? "Selected" : "Apply Now"}
                      </Button>
                    </CardContent>
                  </Card>
                </Grow>
              </Grid>
            ))}
          </Grid>
        </Slide>

        {/* No Schools Message */}
        {schools && schools.length === 0 && (
          <Fade in timeout={2000}>
            <Box sx={{ textAlign: "center", mt: 4 }}>
              <SchoolIcon sx={{ fontSize: 80, color: "rgba(255,255,255,0.5)", mb: 2 }} />
              <Typography variant="h5" sx={{ color: "white", mb: 2 }}>
                No Schools Available
              </Typography>
              <Typography variant="body1" sx={{ color: "rgba(255, 255, 255, 0.8)", mb: 3 }}>
                There are currently no schools available for application.
              </Typography>
              <Button
                variant="contained"
                onClick={handleBackToLogin}
                sx={{
                  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  borderRadius: 2,
                  px: 4,
                  py: 1.5,
                  fontWeight: "bold",
                }}
              >
                {isAuthenticated ? "Back to Dashboard" : "Back to Login"}
              </Button>
            </Box>
          </Fade>
        )}
      </Container>

      {/* Application Form Modal */}
      {selectedSchoolData && (
        <ApplicationFormModal
          open={applicationModalOpen}
          onClose={handleApplicationModalClose}
          onSubmit={handleApplicationSubmit}
          isEdit={false}
          isStudentEdit={false}
          hideAdmissionDetails={true} // Hide admission details when opened from school selection
          schoolData={{
            nameofSchool: selectedSchoolData.name,
            medium: selectedSchoolData.mediums,
            // Add other required school data fields as needed
            blockName: '',
            district: '',
            diseCode: '',
            state: '',
            pinNo: '',
            schoolCode: selectedSchoolData.id.toString(),
            schoolLogo: ''
          }}
          selectedClass={null} // No class selected initially
        />
      )}
    </Box>
  );
};

export default SchoolSelection;
